import React from 'react';
import { Grid } from '@inplayer-org/inplayer-ui';
import { Field, Formik, FormikProps, FieldProps } from 'formik';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { object, string } from 'yup';

// types
import RootState from 'ducks/RootState';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// utils
import { isDevelopmentEnv } from 'utils/helpers';

// actions
import { updateGiftDetails } from 'ducks/merchantDucks/items/actions';

// rules
import Rules from 'restrictions/roles/constants/rules';

// components
import FieldError from 'components/Fields/FieldError';
import { FullHeightForm, ContentContainer, CenterContent, BlueTypography } from 'components/SharedStyledElements';
import CheckboxField from 'components/Fields/CheckboxField';
import AccordionFooter from 'components/AccordionFooter';
import TextEditor from 'components/TextEditor';
import FormAccordionHelper, { SectionType } from 'pages/MerchantPages/Assets//components/AccordionHelper';
import GiftEmailExample from 'pages/MerchantPages/Assets/GiftDetails/GiftEmailExample';
import AttentionMessage from 'components/AttentionMessage';

const { Container, Cell } = Grid;

export const StyledTypography = styled(BlueTypography)`
  margin-bottom: 0.75rem;
`;

interface FormValues {
  isGiftable: boolean;
  giftDescription: string;
}

interface Props {
  closePanel: Function;
  accordionLabel: string;
}

const GiftDetails: React.FC<Props> = ({ closePanel, accordionLabel }) => {
  const dispatch = useDispatch();
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);
  const isGiftable = useSelector((state: RootState) => state.items.itemDetails.is_giftable);
  const giftDescription = useSelector((state: RootState) => state.items.itemDetails.gift_metadata.description);

  const initialValues: FormValues = {
    isGiftable,
    giftDescription,
  };

  const validationSchema = object().shape({
    giftDescription: string().when('isGiftable', (isAssetGiftable: boolean, schema: any) => {
      const answer = schema.test('giftDescription', 'Invalid gift description', (value: string) => {
        if (!isAssetGiftable) {
          return true;
        }

        return value?.replace(/<\/?[^>]+(>|$)/g, '') || false;
      });

      return answer;
    }),
  });

  const onSubmitForm = async (formValues: FormValues) => {
    await dispatch(updateGiftDetails(formValues));
    closePanel();
  };

  const handleCancel = (resetForm: () => void) => () => {
    resetForm();
    closePanel();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmitForm}
      validationSchema={validationSchema}
    >
      {({ resetForm, isValid, isSubmitting, dirty, values }: FormikProps<FormValues>) => {
        const disableSaveButton = !isValid || isSubmitting || !dirty;
        const { isGiftable: isAssetGiftable } = values;
        return (
          <FullHeightForm>
            <FormAccordionHelper
              initialValues={initialValues}
              closePanel={closePanel}
              index={SectionType.GiftDetails}
            />
            <ContentContainer>
              <CenterContent>
                {isDevelopmentEnv && <AttentionMessage />}
                <Container rowGap="1.75rem" columns={12}>
                  <Cell width={12}>
                    <Field name="isGiftable" isInlineLabelField>
                      {({ field: { value, name }, form }: any) => {
                        return (
                          <CheckboxField
                            tag="checkbox_enable_gift_purchase"
                            id={name}
                            name={name}
                            label="Enable gift purchase"
                            inlineLabel
                            checked={value}
                            disabled={isReadOnly}
                            onCheck={(val) => {
                              if (!val) {
                                resetForm();
                                form.setFieldValue('giftDescription', '');
                              }

                              form.setFieldValue('isGiftable', val);
                            }}
                          />
                        );
                      }}
                    </Field>
                  </Cell>
                  <Cell width={12}>
                    <StyledTypography variant="h6">* Gift description</StyledTypography>
                    <Field tag="text_editor_gift_desciription" name="giftDescription">
                      {({ field: { value, onChange, name }, form }: FieldProps) => (
                        <TextEditor
                          value={isAssetGiftable ? value : ''}
                          onChange={onChange(name)}
                          readOnly={isReadOnly || !isAssetGiftable}
                          placeholder="Gift description"
                          onBlur={() => {
                            if (isAssetGiftable) form.setFieldTouched(name);
                          }}
                          // Show only link and clean as toolbar options
                          toolbarOptions={['link', 'clean']}
                        />
                      )}
                    </Field>
                    <FieldError name="giftDescription" />
                  </Cell>
                  <Cell width={12}>
                    <StyledTypography variant="h6">Example email</StyledTypography>
                    <GiftEmailExample />
                  </Cell>
                </Container>
              </CenterContent>
              <AccordionFooter
                accordionLabel={accordionLabel}
                handleCancel={handleCancel(resetForm)}
                disabled={disableSaveButton}
              />
            </ContentContainer>
          </FullHeightForm>
        );
      }}
    </Formik>
  );
};

export default GiftDetails;
